import JSZip from 'jszip';

export interface RawItemBulkRow {
  rowIndex: number;
  itemName: string;
  categoryName: string;
  unit: string;
  hsnCode: string;
  imageFile: File | null;
  imagePreviewUrl: string | null;
}

export interface RawItemBulkParseOptions {
  requireCategory?: boolean;
}

export interface RawItemBulkParseResult {
  rows: RawItemBulkRow[];
  warnings: string[];
}

const NAME_HEADERS = ['item name', 'itemname', 'raw item name', 'raw item', 'part name', 'partname', 'name'];
const CATEGORY_HEADERS = ['category', 'category name', 'categoryname', 'item category'];
const UNIT_HEADERS = ['unit', 'units', 'item unit', 'uom'];
const HSN_HEADERS = ['hsn', 'hsn code', 'hsncode'];
const IMAGE_HEADERS = ['image', 'images', 'pic', 'pic of part', 'picture', 'photo', 'item image'];

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function findColumnIndex(headers: string[], candidates: string[]): number {
  const normalized = headers.map(normalizeHeader);
  for (const candidate of candidates) {
    const idx = normalized.indexOf(candidate);
    if (idx >= 0) return idx;
  }
  return -1;
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      const next = line[i + 1];
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

function colLettersToIndex(col: string): number {
  let n = 0;
  for (let i = 0; i < col.length; i++) {
    n = n * 26 + (col.charCodeAt(i) - 64);
  }
  return n - 1;
}

function parseCellRef(ref: string): { row: number; col: number } | null {
  const match = /^([A-Z]+)(\d+)$/i.exec(ref);
  if (!match) return null;
  return {
    col: colLettersToIndex(match[1].toUpperCase()),
    row: parseInt(match[2], 10) - 1, // 0-based
  };
}

function mimeFromPath(path: string): string {
  const lower = path.toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.bmp')) return 'image/bmp';
  return 'image/jpeg';
}

function extensionFromMime(mime: string): string {
  if (mime === 'image/png') return 'png';
  if (mime === 'image/gif') return 'gif';
  if (mime === 'image/webp') return 'webp';
  return 'jpg';
}

async function fileFromZipEntry(
  zip: JSZip,
  mediaPath: string,
  fileName: string
): Promise<File | null> {
  const entry = zip.file(mediaPath);
  if (!entry) return null;
  const blob = await entry.async('blob');
  const mime = mimeFromPath(mediaPath);
  if (blob.size > MAX_IMAGE_BYTES) {
    return null;
  }
  return new File([blob], fileName, { type: mime });
}

/**
 * Parse a CSV bulk file. CSV cannot embed images — use .xlsx for an image column.
 * Expected headers: item name, category [, unit] [, hsn]
 */
export async function parseRawItemBulkCsv(
  file: File,
  options: RawItemBulkParseOptions = {}
): Promise<RawItemBulkParseResult> {
  const requireCategory = options.requireCategory !== false;
  const text = await file.text();
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length < 2) {
    throw new Error('CSV is empty or missing data rows.');
  }

  const headers = parseCsvLine(lines[0]);
  const nameIdx = findColumnIndex(headers, NAME_HEADERS);
  const categoryIdx = findColumnIndex(headers, CATEGORY_HEADERS);
  const unitIdx = findColumnIndex(headers, UNIT_HEADERS);
  const hsnIdx = findColumnIndex(headers, HSN_HEADERS);
  const imageIdx = findColumnIndex(headers, IMAGE_HEADERS);

  if (nameIdx < 0 || (requireCategory && categoryIdx < 0)) {
    throw new Error(requireCategory ? 'CSV must include columns: item name, category' : 'CSV must include an item name column');
  }

  const warnings: string[] = [];
  if (imageIdx >= 0) {
    warnings.push('CSV cannot embed images. Use an .xlsx file with pictures in the Image column.');
  }

  const rows: RawItemBulkRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const itemName = (cols[nameIdx] || '').trim();
    const categoryName = categoryIdx >= 0 ? (cols[categoryIdx] || '').trim() : '';
    if (!itemName && !categoryName) continue;

    rows.push({
      rowIndex: i + 1,
      itemName,
      categoryName,
      unit: unitIdx >= 0 ? (cols[unitIdx] || '').trim() : '',
      hsnCode: hsnIdx >= 0 ? (cols[hsnIdx] || '').trim() : '',
      imageFile: null,
      imagePreviewUrl: null,
    });
  }

  return { rows, warnings };
}

function getXmlLocalName(el: Element): string {
  return el.localName || el.tagName.replace(/^.*:/, '');
}

function findChild(el: Element, localName: string): Element | null {
  for (const child of Array.from(el.children)) {
    if (getXmlLocalName(child) === localName) return child;
  }
  return null;
}

function findDescendant(el: Element, localName: string): Element | null {
  if (getXmlLocalName(el) === localName) return el;
  for (const child of Array.from(el.children)) {
    const found = findDescendant(child, localName);
    if (found) return found;
  }
  return null;
}

function textOf(el: Element | null, localName: string): string {
  if (!el) return '';
  const child = findChild(el, localName);
  return child?.textContent?.trim() || '';
}

/**
 * Parse an .xlsx bulk file. Supports embedded pictures mapped to the Image column
 * (or any picture anchored on the data row as fallback).
 */
export async function parseRawItemBulkXlsx(
  file: File,
  options: RawItemBulkParseOptions = {}
): Promise<RawItemBulkParseResult> {
  const requireCategory = options.requireCategory !== false;
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const warnings: string[] = [];

  // Shared strings
  const sharedStrings: string[] = [];
  const ssFile = zip.file('xl/sharedStrings.xml');
  if (ssFile) {
    const ssXml = await ssFile.async('text');
    const ssDoc = new DOMParser().parseFromString(ssXml, 'application/xml');
    const sis = Array.from(ssDoc.getElementsByTagName('*')).filter((el) => getXmlLocalName(el) === 'si');
    for (const si of sis) {
      const texts = Array.from(si.getElementsByTagName('*'))
        .filter((el) => getXmlLocalName(el) === 't')
        .map((t) => t.textContent || '');
      sharedStrings.push(texts.join(''));
    }
  }

  // First worksheet path via workbook relationships
  const workbookXml = await zip.file('xl/workbook.xml')?.async('text');
  if (!workbookXml) throw new Error('Invalid Excel file: missing workbook.xml');
  const workbookRelsXml = await zip.file('xl/_rels/workbook.xml.rels')?.async('text');
  if (!workbookRelsXml) throw new Error('Invalid Excel file: missing workbook relationships');

  const wbDoc = new DOMParser().parseFromString(workbookXml, 'application/xml');
  const relsDoc = new DOMParser().parseFromString(workbookRelsXml, 'application/xml');
  const ridToTarget = new Map<string, string>();
  Array.from(relsDoc.getElementsByTagName('*'))
    .filter((el) => getXmlLocalName(el) === 'Relationship')
    .forEach((rel) => {
      const id = rel.getAttribute('Id');
      const target = rel.getAttribute('Target');
      if (id && target) ridToTarget.set(id, target);
    });

  const firstSheet = Array.from(wbDoc.getElementsByTagName('*')).find((el) => getXmlLocalName(el) === 'sheet');
  if (!firstSheet) throw new Error('Excel file has no sheets');
  const sheetRid =
    firstSheet.getAttribute('r:id') ||
    firstSheet.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id');
  if (!sheetRid || !ridToTarget.has(sheetRid)) {
    throw new Error('Could not resolve worksheet path');
  }
  let sheetPath = ridToTarget.get(sheetRid)!;
  if (!sheetPath.startsWith('xl/')) {
    sheetPath = `xl/${sheetPath.replace(/^\//, '')}`;
  }

  const sheetXml = await zip.file(sheetPath)?.async('text');
  if (!sheetXml) throw new Error('Could not read worksheet');
  const sheetDoc = new DOMParser().parseFromString(sheetXml, 'application/xml');

  // Excel anchors a picture at a row plus a pixel offset (rowOff), not at a row index.
  // When a row is taller than default (common when a picture is pasted into it), the
  // offset can push the picture's true position into the next row(s). Resolve the
  // anchor's effective row by walking forward through actual row heights.
  const EMU_PER_POINT = 12700;
  const sheetFormatPr = Array.from(sheetDoc.getElementsByTagName('*')).find(
    (el) => getXmlLocalName(el) === 'sheetFormatPr'
  );
  const parsedDefaultHeight = sheetFormatPr
    ? parseFloat(sheetFormatPr.getAttribute('defaultRowHeight') || '')
    : NaN;
  const defaultRowHeightPt = Number.isFinite(parsedDefaultHeight) && parsedDefaultHeight > 0 ? parsedDefaultHeight : 15;

  const rowHeightPtByIndex = new Map<number, number>();
  Array.from(sheetDoc.getElementsByTagName('*'))
    .filter((el) => getXmlLocalName(el) === 'row')
    .forEach((rowEl) => {
      const rAttr = rowEl.getAttribute('r');
      if (!rAttr) return;
      const rowIdx0 = parseInt(rAttr, 10) - 1;
      const ht = parseFloat(rowEl.getAttribute('ht') || '');
      if (Number.isFinite(rowIdx0) && Number.isFinite(ht) && ht > 0) {
        rowHeightPtByIndex.set(rowIdx0, ht);
      }
    });

  const resolveEffectiveRow = (rowIdx0: number, rowOffEmu: number): number => {
    let row = rowIdx0;
    let remaining = Number.isFinite(rowOffEmu) ? rowOffEmu : 0;
    for (let i = 0; i < 10000; i++) {
      const heightEmu = (rowHeightPtByIndex.get(row) ?? defaultRowHeightPt) * EMU_PER_POINT;
      if (heightEmu <= 0 || remaining < heightEmu) break;
      remaining -= heightEmu;
      row += 1;
    }
    return row;
  };

  // Build sparse cell map: row -> col -> value
  const cellMap = new Map<number, Map<number, string>>();
  const cells = Array.from(sheetDoc.getElementsByTagName('*')).filter((el) => getXmlLocalName(el) === 'c');
  for (const cell of cells) {
    const ref = cell.getAttribute('r');
    if (!ref) continue;
    const parsed = parseCellRef(ref);
    if (!parsed) continue;
    const t = cell.getAttribute('t');
    const vEl = findChild(cell, 'v');
    let value = '';
    if (t === 's' && vEl?.textContent != null) {
      const idx = parseInt(vEl.textContent, 10);
      value = Number.isFinite(idx) ? sharedStrings[idx] || '' : '';
    } else if (t === 'inlineStr') {
      const isEl = findChild(cell, 'is');
      const tNodes = isEl
        ? Array.from(isEl.getElementsByTagName('*')).filter((el) => getXmlLocalName(el) === 't')
        : [];
      value = tNodes.map((n) => n.textContent || '').join('');
    } else if (vEl?.textContent != null) {
      value = vEl.textContent;
    }
    if (!cellMap.has(parsed.row)) cellMap.set(parsed.row, new Map());
    cellMap.get(parsed.row)!.set(parsed.col, value);
  }

  // Find header row (first non-empty row that contains name + category headers)
  const sortedRows = Array.from(cellMap.keys()).sort((a, b) => a - b);
  let headerRow = -1;
  let headers: string[] = [];
  for (const row of sortedRows) {
    const cols = cellMap.get(row)!;
    const maxCol = Math.max(...Array.from(cols.keys()), 0);
    const rowHeaders: string[] = [];
    for (let c = 0; c <= maxCol; c++) {
      rowHeaders.push(cols.get(c) || '');
    }
    const nameIdx = findColumnIndex(rowHeaders, NAME_HEADERS);
    const categoryIdx = findColumnIndex(rowHeaders, CATEGORY_HEADERS);
    if (nameIdx >= 0 && (!requireCategory || categoryIdx >= 0)) {
      headerRow = row;
      headers = rowHeaders;
      break;
    }
  }

  if (headerRow < 0) {
    throw new Error(
      requireCategory
        ? 'Excel must include a header row with columns: item name, category'
        : 'Excel must include a header row with an item name column'
    );
  }

  const nameIdx = findColumnIndex(headers, NAME_HEADERS);
  const categoryIdx = findColumnIndex(headers, CATEGORY_HEADERS);
  const unitIdx = findColumnIndex(headers, UNIT_HEADERS);
  const hsnIdx = findColumnIndex(headers, HSN_HEADERS);
  let imageColIdx = findColumnIndex(headers, IMAGE_HEADERS);

  // Extract images from drawings
  const sheetDir = sheetPath.substring(0, sheetPath.lastIndexOf('/'));
  const sheetName = sheetPath.substring(sheetPath.lastIndexOf('/') + 1);
  const sheetRelsPath = `${sheetDir}/_rels/${sheetName}.rels`;
  const sheetRelsXml = await zip.file(sheetRelsPath)?.async('text');

  type ImageAnchor = { row: number; col: number; file: File; previewUrl: string };
  const imagesByRow = new Map<number, ImageAnchor[]>();

  if (sheetRelsXml) {
    const sheetRelsDoc = new DOMParser().parseFromString(sheetRelsXml, 'application/xml');
    const drawingRels = Array.from(sheetRelsDoc.getElementsByTagName('*')).filter((el) => {
      if (getXmlLocalName(el) !== 'Relationship') return false;
      const type = el.getAttribute('Type') || '';
      return type.includes('/drawing') || (el.getAttribute('Target') || '').includes('drawings/');
    });

    for (const drawingRel of drawingRels) {
      let drawingTarget = drawingRel.getAttribute('Target') || '';
      if (!drawingTarget) continue;
      // Resolve relative to sheet dir
      if (drawingTarget.startsWith('/')) {
        drawingTarget = drawingTarget.replace(/^\//, '');
        if (!drawingTarget.startsWith('xl/')) drawingTarget = `xl/${drawingTarget}`;
      } else {
        // e.g. ../drawings/drawing1.xml from xl/worksheets/
        const baseParts = sheetDir.split('/');
        const targetParts = drawingTarget.split('/');
        for (const part of targetParts) {
          if (part === '..') baseParts.pop();
          else if (part !== '.') baseParts.push(part);
        }
        drawingTarget = baseParts.join('/');
      }

      const drawingXml = await zip.file(drawingTarget)?.async('text');
      if (!drawingXml) continue;

      const drawingDir = drawingTarget.substring(0, drawingTarget.lastIndexOf('/'));
      const drawingName = drawingTarget.substring(drawingTarget.lastIndexOf('/') + 1);
      const drawingRelsPath = `${drawingDir}/_rels/${drawingName}.rels`;
      const drawingRelsXml = await zip.file(drawingRelsPath)?.async('text');
      const embedToMedia = new Map<string, string>();
      if (drawingRelsXml) {
        const dRelsDoc = new DOMParser().parseFromString(drawingRelsXml, 'application/xml');
        Array.from(dRelsDoc.getElementsByTagName('*'))
          .filter((el) => getXmlLocalName(el) === 'Relationship')
          .forEach((rel) => {
            const id = rel.getAttribute('Id');
            let target = rel.getAttribute('Target') || '';
            if (!id || !target) return;
            if (target.startsWith('/')) {
              target = target.replace(/^\//, '');
              if (!target.startsWith('xl/')) target = `xl/${target}`;
            } else {
              const baseParts = drawingDir.split('/');
              const targetParts = target.split('/');
              for (const part of targetParts) {
                if (part === '..') baseParts.pop();
                else if (part !== '.') baseParts.push(part);
              }
              target = baseParts.join('/');
            }
            embedToMedia.set(id, target);
          });
      }

      const drawingDoc = new DOMParser().parseFromString(drawingXml, 'application/xml');
      const anchors = Array.from(drawingDoc.documentElement.children).filter((el) => {
        const name = getXmlLocalName(el);
        return name === 'oneCellAnchor' || name === 'twoCellAnchor';
      });

      let imageSeq = 0;
      for (const anchor of anchors) {
        const fromEl = findChild(anchor, 'from');
        const rowStr = textOf(fromEl, 'row');
        const colStr = textOf(fromEl, 'col');
        if (rowStr === '' || colStr === '') continue;
        const rawRow = parseInt(rowStr, 10);
        const col = parseInt(colStr, 10);
        if (!Number.isFinite(rawRow) || !Number.isFinite(col)) continue;
        const rowOffStr = textOf(fromEl, 'rowOff');
        const rowOffEmu = rowOffStr ? parseInt(rowOffStr, 10) : 0;
        const row = resolveEffectiveRow(rawRow, rowOffEmu);

        let embedId: string | null = null;
        const blip = findDescendant(anchor, 'blip');
        if (blip) {
          embedId =
            blip.getAttribute('r:embed') ||
            blip.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'embed');
        }
        if (!embedId) continue;
        const mediaPath = embedToMedia.get(embedId);
        if (!mediaPath) continue;

        imageSeq += 1;
        const mime = mimeFromPath(mediaPath);
        const ext = extensionFromMime(mime);
        const imageFile = await fileFromZipEntry(zip, mediaPath, `row-${row + 1}-image-${imageSeq}.${ext}`);
        if (!imageFile) {
          warnings.push(`Skipped oversized or missing image for Excel row ${row + 1}`);
          continue;
        }
        const previewUrl = URL.createObjectURL(imageFile);
        const list = imagesByRow.get(row) || [];
        list.push({ row, col, file: imageFile, previewUrl });
        imagesByRow.set(row, list);
      }
    }
  } else {
    warnings.push('No drawing relationships found; embedded images (if any) were not read.');
  }

  // If no explicit image header but images exist, infer image column from most common image col
  if (imageColIdx < 0 && imagesByRow.size > 0) {
    const colCounts = new Map<number, number>();
    imagesByRow.forEach((list) => {
      list.forEach((img) => colCounts.set(img.col, (colCounts.get(img.col) || 0) + 1));
    });
    let bestCol = -1;
    let bestCount = 0;
    colCounts.forEach((count, col) => {
      if (count > bestCount) {
        bestCount = count;
        bestCol = col;
      }
    });
    if (bestCol >= 0) {
      imageColIdx = bestCol;
      warnings.push(`No Image header found; using column index ${bestCol + 1} from embedded pictures.`);
    }
  }

  const usedPreviewUrls = new Set<string>();
  const rows: RawItemBulkRow[] = [];

  for (const row of sortedRows) {
    if (row <= headerRow) continue;
    const cols = cellMap.get(row)!;
    const itemName = (cols.get(nameIdx) || '').trim();
    const categoryName = categoryIdx >= 0 ? (cols.get(categoryIdx) || '').trim() : '';
    if (!itemName && !categoryName) continue;

    const unit = unitIdx >= 0 ? (cols.get(unitIdx) || '').trim() : '';
    const hsnCode = hsnIdx >= 0 ? (cols.get(hsnIdx) || '').trim() : '';

    const rowImages = imagesByRow.get(row) || [];
    let chosen: ImageAnchor | null = null;
    if (imageColIdx >= 0) {
      chosen = rowImages.find((img) => img.col === imageColIdx) || null;
    }
    if (!chosen && rowImages.length === 1) {
      chosen = rowImages[0];
    } else if (!chosen && rowImages.length > 1) {
      // Prefer closest to image column, else first
      if (imageColIdx >= 0) {
        chosen = [...rowImages].sort(
          (a, b) => Math.abs(a.col - imageColIdx) - Math.abs(b.col - imageColIdx)
        )[0];
      } else {
        chosen = rowImages[0];
      }
      warnings.push(`Multiple images on Excel row ${row + 1}; using one of them.`);
    }

    if (chosen) usedPreviewUrls.add(chosen.previewUrl);

    rows.push({
      rowIndex: row + 1,
      itemName,
      categoryName,
      unit,
      hsnCode,
      imageFile: chosen?.file || null,
      imagePreviewUrl: chosen?.previewUrl || null,
    });
  }

  // Revoke unused preview URLs from unmatched images
  imagesByRow.forEach((list) => {
    list.forEach((img) => {
      if (!usedPreviewUrls.has(img.previewUrl)) {
        URL.revokeObjectURL(img.previewUrl);
      }
    });
  });

  if (rows.length === 0) {
    throw new Error('No data rows found under the header.');
  }

  return { rows, warnings };
}

export async function parseRawItemBulkFile(
  file: File,
  options: RawItemBulkParseOptions = {}
): Promise<RawItemBulkParseResult> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || file.type === 'text/csv') {
    return parseRawItemBulkCsv(file, options);
  }
  if (name.endsWith('.xlsx') || name.endsWith('.xlsm')) {
    return parseRawItemBulkXlsx(file, options);
  }
  if (name.endsWith('.xls')) {
    throw new Error('Legacy .xls is not supported. Please save as .xlsx or .csv.');
  }
  throw new Error('Unsupported file type. Please upload .xlsx or .csv.');
}

export function revokeBulkPreviewUrls(rows: RawItemBulkRow[]) {
  rows.forEach((row) => {
    if (row.imagePreviewUrl) {
      URL.revokeObjectURL(row.imagePreviewUrl);
    }
  });
}
